import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";
import { useNavigate } from "react-router-dom";
import PermissionGroupService from "../../services/tenantServices/PermissionGroupService";
import TenantLayout from "../../layouts/TenantLayout";
import SecurityIcon from "@mui/icons-material/Security";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import LockIcon from "@mui/icons-material/Lock";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { tenantPanelContentSx } from "./tenantPanelContentSx";

export default function PermissionGroupsList() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const permissionService = new PermissionGroupService();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const data = await permissionService.getAllGroups();
      setGroups(data);
    } catch (error) {
      console.error("Error fetching permission groups:", error);
      setSnackbar({
        open: true,
        message: "Error loading permission groups",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (group) => {
    if (group.is_default) {
      setSnackbar({
        open: true,
        message: "Default groups cannot be deleted",
        severity: "warning",
      });
      return;
    }
    setSelectedGroup(group);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await permissionService.deleteGroup(selectedGroup.id);
      setSnackbar({
        open: true,
        message: "Permission group deleted successfully",
        severity: "success",
      });
      setDeleteDialogOpen(false);
      setSelectedGroup(null);
      fetchGroups();
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Error deleting permission group";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    }
  };

  const countPermissions = (group) => {
    const permissions = Object.keys(group).filter(key => 
      key.startsWith('can_') && group[key] === true
    );
    return permissions.length;
  };

  if (loading) {
    return (
      <TenantLayout contentSx={tenantPanelContentSx}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <Typography variant="h5" sx={{ color: "#64748b" }}>
            Loading permission groups...
          </Typography>
        </Box>
      </TenantLayout>
    );
  }

  return (
    <TenantLayout contentSx={tenantPanelContentSx}>
      <Box sx={{ width: "100%", maxWidth: "100%" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <SecurityIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
          <Box>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, #1e293b 0%, ${colors.blueAccent[600]} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Permission Groups
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
              Manage access control groups for your team
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/tenant/permission-groups/create")}
          sx={{
            background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
            color: "white",
            px: 3,
            py: 1.5,
            borderRadius: "12px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: `0 4px 12px ${colors.greenAccent[500]}40`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 6px 16px ${colors.greenAccent[500]}60`,
            },
            transition: "all 0.3s ease",
          }}
        >
          Create New Group
        </Button>
      </Box>

      {/* Groups Grid */}
      {groups.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 3,
            borderRadius: "16px",
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
          }}
        >
          <SecurityIcon sx={{ fontSize: 64, color: "#94a3b8", mb: 2 }} />
          <Typography variant="h5" sx={{ color: "#475569", mb: 1 }}>
            No Permission Groups Yet
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mb: 3 }}>
            Create your first permission group to start managing team access
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/tenant/permission-groups/create")}
            sx={{
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
              color: "white",
              px: 4,
              py: 1.5,
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Create First Group
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {groups.map((group) => (
            <Grid item xs={12} md={6} lg={4} key={group.id}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
                  border: `1px solid ${colors.grey[800]}30`,
                  borderRadius: "16px",
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 8px 24px rgba(0, 0, 0, 0.2)`,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Header */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "12px",
                          background: group.type === 'admin' 
                            ? `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`
                            : `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {group.type === 'admin' ? (
                          <AdminPanelSettingsIcon sx={{ color: "white", fontSize: 24 }} />
                        ) : (
                          <MedicalServicesIcon sx={{ color: "white", fontSize: 24 }} />
                        )}
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 700 }}>
                          {group.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                          {group.name}
                        </Typography>
                      </Box>
                    </Box>

                    {group.is_default && (
                      <Tooltip title="Default group - cannot be deleted">
                        <Chip
                          icon={<LockIcon sx={{ fontSize: 14 }} />}
                          label="Default"
                          size="small"
                          sx={{
                            backgroundColor: `${colors.blueAccent[800]}40`,
                            color: colors.blueAccent[300],
                            fontWeight: 600,
                            border: `1px solid ${colors.blueAccent[500]}40`,
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.grey[300],
                      mb: 2,
                      minHeight: "40px",
                      fontSize: "0.875rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {group.description || "No description provided"}
                  </Typography>

                  {/* Stats */}
                  <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        flex: 1,
                        p: 1.5,
                        borderRadius: "10px",
                        background: `${colors.blueAccent[800]}20`,
                        border: `1px solid ${colors.blueAccent[500]}30`,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mb: 0.5 }}>
                        Type
                      </Typography>
                      <Chip
                        label={group.type.toUpperCase()}
                        size="small"
                        sx={{
                          backgroundColor: group.type === 'admin' ? colors.blueAccent[700] : colors.greenAccent[700],
                          color: "white",
                          fontWeight: 600,
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        p: 1.5,
                        borderRadius: "10px",
                        background: `${colors.greenAccent[800]}20`,
                        border: `1px solid ${colors.greenAccent[500]}30`,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mb: 0.5 }}>
                        Permissions
                      </Typography>
                      <Typography variant="h6" sx={{ color: colors.greenAccent[300], fontWeight: 700 }}>
                        {countPermissions(group)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/tenant/permission-groups/${group.id}`)}
                        sx={{
                          flex: 1,
                          borderRadius: "8px",
                          backgroundColor: `${colors.blueAccent[700]}40`,
                          border: `1px solid ${colors.blueAccent[500]}40`,
                          "&:hover": {
                            backgroundColor: `${colors.blueAccent[600]}60`,
                          },
                        }}
                      >
                        <VisibilityIcon sx={{ fontSize: 18, color: colors.blueAccent[300] }} />
                      </IconButton>
                    </Tooltip>

                    {!group.is_default && (
                      <>
                        <Tooltip title="Edit Group">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/tenant/permission-groups/edit/${group.id}`)}
                            sx={{
                              flex: 1,
                              borderRadius: "8px",
                              backgroundColor: `${colors.greenAccent[700]}40`,
                              border: `1px solid ${colors.greenAccent[500]}40`,
                              "&:hover": {
                                backgroundColor: `${colors.greenAccent[600]}60`,
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 18, color: colors.greenAccent[300] }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete Group">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(group)}
                            sx={{
                              flex: 1,
                              borderRadius: "8px",
                              backgroundColor: `${colors.redAccent[700]}40`,
                              border: `1px solid ${colors.redAccent[500]}40`,
                              "&:hover": {
                                backgroundColor: `${colors.redAccent[600]}60`,
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 18, color: colors.redAccent[300] }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Permission Group"
        description={
          selectedGroup
            ? `Are you sure you want to delete the permission group "${selectedGroup.label}"? This action cannot be undone.`
            : ""
        }
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </TenantLayout>
  );
}

